import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  ChallengeApiRecord,
  UpdateChallengeRequest,
  UpdateChallengeResponse,
} from "../../types/gamePass";
import type { ChallengeDraft } from "../../components/GamePass/CreateSeason/createSeasonData";
import { getPrivateInstance } from "../_privateInstance";
import { unwrapGamePassEntity } from "./responseMappers";

const buildUpdateChallengeUrl = (challengeId: string): string =>
  API_ROUTES.UPDATE_CHALLENGE.replace("{challenge_id}", challengeId);

/**
 * Convert the challenge editor's local draft into the partial
 * `PUT /game-pass-admin/v2/admin/challenges/{challenge_id}` body.
 *
 * Rewards are persisted separately via
 * `PUT /game-pass-admin/v2/admin/challenges/{challenge_id}/reward` — see
 * `useUpdateChallengeReward`. */
export const mapChallengeDraftToUpdateChallengeRequest = (
  draft: ChallengeDraft,
  sortOrder: number,
  /** S3 object key for the challenge icon, resolved by the caller. Three
   *  states drive the wire payload:
   *  - `undefined` → omit `img_key` entirely (edit didn't touch the icon, so
   *    the stored value is left untouched).
   *  - a non-empty string → a newly-uploaded icon (sent as-is).
   *  - `null` → the admin explicitly removed the icon (sent as `img_key: null`
   *    so the backend clears it). */
  imgKey?: string | null,
): UpdateChallengeRequest => ({
  name: draft.name.trim(),
  sort_order: sortOrder,
  description: draft.description.trim(),
  ...(imgKey !== undefined ? { img_key: imgKey } : {}),
});

export type UpdateChallengeVariables = {
  challengeId: string;
  seasonId?: string | null;
  body: UpdateChallengeRequest;
};

const putUpdateChallenge = async ({
  challengeId,
  body,
}: UpdateChallengeVariables): Promise<ChallengeApiRecord> => {
  const response = await getPrivateInstance().put<UpdateChallengeResponse>(
    buildUpdateChallengeUrl(challengeId),
    body,
  );
  return unwrapGamePassEntity(response.data, "challenge");
};

export const useUpdateChallenge = (): UseMutationResult<
  ChallengeApiRecord,
  Error,
  UpdateChallengeVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "challenges", "update"],
    mutationFn: putUpdateChallenge,
    onSuccess: (_challenge, variables) => {
      if (variables.seasonId) {
        // Keep the in-place Season Details editor in sync with the saved
        // challenge metadata (name / order / description).
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "challenges", "list", variables.seasonId],
        });
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "seasons", "detail", variables.seasonId],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "challenges", "list"],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};
